import React, { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Plus, Search, ShieldAlert, UserCog } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { ROLES, ADMIN_ROLES } from "@/config/navigation";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function StatusDot({ active }) {
    return (
        <span className="inline-flex items-center gap-1.5 text-xs text-zinc-700">
            <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-zinc-300"}`} />
            {active ? "Active" : "Inactive"}
        </span>
    );
}

function CreateUserDialog({ onCreated }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ email: "", name: "", role: "sales", password: "" });
    const [temp, setTemp] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const payload = { ...form };
            if (!payload.password) delete payload.password;
            const { data } = await api.post("/users", payload);
            setTemp(data.temporary_password);
            onCreated();
            toast.success("User created");
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const reset = () => {
        setForm({ email: "", name: "", role: "sales", password: "" });
        setTemp(null);
        setError("");
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(v) => {
                setOpen(v);
                if (!v) reset();
            }}
        >
            <DialogTrigger asChild>
                <Button size="sm" data-testid="create-user-button">
                    <Plus className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.5} /> Add user
                </Button>
            </DialogTrigger>
            <DialogContent data-testid="create-user-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">Add a new user</DialogTitle>
                    <DialogDescription>
                        Users are provisioned here. No public sign-up exists.
                    </DialogDescription>
                </DialogHeader>
                {temp ? (
                    <div className="space-y-3">
                        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                            User created successfully. Share these credentials securely.
                        </div>
                        <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3 font-mono text-xs">
                            <div>Email: {form.email}</div>
                            <div>Temporary password: {temp}</div>
                        </div>
                        <DialogFooter>
                            <Button onClick={() => setOpen(false)} data-testid="create-user-done">
                                Done
                            </Button>
                        </DialogFooter>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-4">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="space-y-1.5">
                                <Label className="text-xs">Full name</Label>
                                <Input
                                    required
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    data-testid="create-user-name"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">Work email</Label>
                                <Input
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    data-testid="create-user-email"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="space-y-1.5">
                                <Label className="text-xs">Role</Label>
                                <Select
                                    value={form.role}
                                    onValueChange={(v) => setForm({ ...form, role: v })}
                                >
                                    <SelectTrigger data-testid="create-user-role">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ROLES.map((r) => (
                                            <SelectItem key={r.value} value={r.value}>
                                                {r.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">Password (optional)</Label>
                                <Input
                                    type="text"
                                    placeholder="Auto-generate if empty"
                                    value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                    data-testid="create-user-password"
                                />
                            </div>
                        </div>
                        {error && (
                            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                                {error}
                            </div>
                        )}
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setOpen(false)}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" disabled={loading} data-testid="create-user-submit">
                                {loading ? "Creating…" : "Create user"}
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default function UserManagement() {
    const { user } = useAuth();
    const isAdmin = user && ADMIN_ROLES.includes(user.role);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [roleFilter, setRoleFilter] = useState("all");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/users");
            setUsers(data.users || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isAdmin) load();
        else setLoading(false);
    }, [isAdmin]);

    const toggleActive = async (u) => {
        try {
            await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
            toast.success(`User ${u.is_active ? "deactivated" : "activated"}`);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const changeRole = async (u, role) => {
        try {
            await api.patch(`/users/${u.id}`, { role });
            toast.success("Role updated");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const resetPassword = async (u) => {
        const pw = prompt(`New password for ${u.email} (min 8 chars):`);
        if (!pw || pw.length < 8) return;
        try {
            await api.post(`/users/${u.id}/reset-password`, { new_password: pw });
            toast.success("Password reset");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const filtered = users.filter((u) => {
        const matchesQ =
            !q ||
            u.email.toLowerCase().includes(q.toLowerCase()) ||
            (u.name || "").toLowerCase().includes(q.toLowerCase());
        const matchesRole = roleFilter === "all" || u.role === roleFilter;
        return matchesQ && matchesRole;
    });

    if (!isAdmin) {
        return (
            <div data-testid="page-user-management">
                <PageHeader title="User Management" description="Restricted to administrators." />
                <div className="mx-8 my-8 flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                    <ShieldAlert className="h-4 w-4" strokeWidth={1.5} />
                    You do not have permission to view this page.
                </div>
            </div>
        );
    }

    return (
        <div data-testid="page-user-management">
            <PageHeader
                title="User Management"
                description="Create users, assign roles, and manage access across the organisation."
                actions={<CreateUserDialog onCreated={load} />}
            />
            <div className="p-8 space-y-4 max-w-[1400px]">
                <div className="flex items-center gap-2">
                    <div className="relative flex-1 max-w-xs">
                        <Search
                            className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400"
                            strokeWidth={1.5}
                        />
                        <Input
                            placeholder="Search users…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="pl-8 h-9"
                            data-testid="user-search"
                        />
                    </div>
                    <Select value={roleFilter} onValueChange={setRoleFilter}>
                        <SelectTrigger className="w-40 h-9" data-testid="user-role-filter">
                            <SelectValue placeholder="All roles" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All roles</SelectItem>
                            {ROLES.map((r) => (
                                <SelectItem key={r.value} value={r.value}>
                                    {r.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <div className="ml-auto text-xs text-zinc-500 font-mono">
                        {filtered.length} of {users.length}
                    </div>
                </div>

                <div className="rounded-md border border-zinc-200 bg-white overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-zinc-50/60">
                                <TableHead className="text-[10px] uppercase tracking-widest text-zinc-500">User</TableHead>
                                <TableHead className="text-[10px] uppercase tracking-widest text-zinc-500">Role</TableHead>
                                <TableHead className="text-[10px] uppercase tracking-widest text-zinc-500">Status</TableHead>
                                <TableHead className="text-[10px] uppercase tracking-widest text-zinc-500">Last login</TableHead>
                                <TableHead className="w-10" />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={5} className="py-12 text-center text-sm text-zinc-500">
                                        Loading users…
                                    </TableCell>
                                </TableRow>
                            ) : filtered.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} className="py-12 text-center text-sm text-zinc-500">
                                        No users match your filters.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filtered.map((u) => (
                                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-zinc-900 text-white text-[10px] font-semibold">
                                                    {(u.name || u.email)
                                                        .split(" ")
                                                        .map((p) => p[0])
                                                        .slice(0, 2)
                                                        .join("")
                                                        .toUpperCase()}
                                                </div>
                                                <div>
                                                    <div className="text-sm font-medium text-zinc-900">{u.name || "—"}</div>
                                                    <div className="text-xs text-zinc-500">{u.email}</div>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="secondary" className="text-[10px] uppercase tracking-widest">
                                                {u.role}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <StatusDot active={u.is_active} />
                                        </TableCell>
                                        <TableCell className="text-xs text-zinc-600 font-mono">
                                            {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}
                                        </TableCell>
                                        <TableCell>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8"
                                                        data-testid={`user-actions-${u.id}`}
                                                    >
                                                        <MoreHorizontal className="h-4 w-4" strokeWidth={1.5} />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem onClick={() => resetPassword(u)}>
                                                        Reset password
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => toggleActive(u)}>
                                                        {u.is_active ? "Deactivate" : "Activate"}
                                                    </DropdownMenuItem>
                                                    <DropdownMenuSeparator />
                                                    {ROLES.map((r) => (
                                                        <DropdownMenuItem
                                                            key={r.value}
                                                            onClick={() => changeRole(u, r.value)}
                                                            disabled={u.role === r.value}
                                                        >
                                                            <UserCog className="mr-2 h-3 w-3" strokeWidth={1.5} />
                                                            Set role · {r.label}
                                                        </DropdownMenuItem>
                                                    ))}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </div>
    );
}
